<template>
  <div class="payslip-page p-3">
    <div class="text-right mb-3 no-print" v-if="payroll">
      <b-button variant="secondary" size="sm" class="mr-2" @click="$router.push('/manpowers/office-payroll/' + payroll.id)">
        <font-awesome-icon icon="chevron-left" /> Back
      </b-button>
      <b-button variant="primary" size="sm" @click="printNow">
        <font-awesome-icon icon="print" /> Print
      </b-button>
    </div>

    <div v-if="payroll">
      <!-- One printed page per employee, holding two copies of the same payslip
           (cut along the dashed line). -->
      <div v-for="(d, i) in payroll.office_payroll_details" :key="i" class="payslip-sheet">
        <div v-for="(copy, c) in copies" :key="c" class="payslip" :class="{ 'payslip-second': c > 0 }">
          <div class="copy-label">{{ copy }}</div>
          <div class="row no-gutters">
            <!-- Main copy -->
            <div class="col-8 pr-2">
              <div class="slip-box">
                <div class="company">{{ company.NAME }}</div>
                <table class="w-100 head">
                  <tr>
                    <td><strong>EMPLOYEE:</strong> {{ d.manpower ? d.manpower.full_name : '' }}</td>
                    <td class="text-right text-uppercase">{{ position(d) }}</td>
                  </tr>
                  <tr>
                    <td colspan="2"><strong>For the period:</strong> {{ period }}<span v-if="payroll.payroll_group"> &middot; {{ payroll.payroll_group.name }}</span></td>
                  </tr>
                </table>

                <table class="w-100 lines mt-2">
                  <tr class="section">
                    <td><strong>BASIC PAY:</strong></td>
                    <td class="text-center">{{ num(d.payslip.days, 0) }} days ({{ num(d.payslip.total_hours, 2) }} hrs)</td>
                    <td class="text-center">@ {{ num(d.payslip.daily_rate) }} <small v-if="d.payslip.duty_hours">/ {{ num(d.payslip.duty_hours, 0) }}h</small></td>
                    <td class="text-right">{{ num(d.payslip.basic_pay) }}</td>
                  </tr>
                  <tr><td colspan="4"><strong>ADDITIONAL PAY:</strong></td></tr>
                  <tr>
                    <td class="pl-3">Night Diff.</td>
                    <td class="text-center">{{ num(d.payslip.nd_hours, 2) }} hour(s)</td>
                    <td class="text-center">@ {{ num(d.payslip.nd_rate) }}</td>
                    <td class="text-right">{{ num(d.payslip.nd_amount) }}</td>
                  </tr>
                  <tr>
                    <td class="pl-3">Overtime</td>
                    <td class="text-center">{{ num(d.payslip.ot_hours, 2) }} hour(s)</td>
                    <td class="text-center">@ {{ num(d.payslip.ot_rate) }}</td>
                    <td class="text-right">{{ num(d.payslip.ot_amount) }}</td>
                  </tr>
                  <tr>
                    <td class="pl-3">Regular Holiday</td>
                    <td class="text-center">{{ num(d.payslip.regular_holiday_hours, 2) }} hour(s)</td>
                    <td class="text-center">@ {{ num(d.payslip.daily_rate) }}</td>
                    <td class="text-right">{{ num(d.payslip.regular_holiday) }}</td>
                  </tr>
                  <tr>
                    <td class="pl-3">Special Holiday</td>
                    <td class="text-center">{{ num(d.payslip.special_holiday_hours, 2) }} hour(s)</td>
                    <td class="text-center">@ {{ num(d.payslip.daily_rate) }}</td>
                    <td class="text-right">{{ num(d.payslip.special_holiday) }}</td>
                  </tr>
                  <tr>
                    <td class="pl-3">Other Adjustment</td>
                    <td class="text-center">Pay Adj.</td>
                    <td></td>
                    <td class="text-right">{{ num(d.payslip.other_adjustment) }}</td>
                  </tr>
                  <tr><td colspan="4"><strong>DEDUCTIONS:</strong></td></tr>
                  <tr><td class="pl-3">Pag-Ibig</td><td></td><td></td><td class="text-right">{{ num(d.payslip.pagibig) }}</td></tr>
                  <tr><td class="pl-3">PhilHealth</td><td></td><td></td><td class="text-right">{{ num(d.payslip.philhealth) }}</td></tr>
                  <tr><td class="pl-3">SSS</td><td></td><td></td><td class="text-right">{{ num(d.payslip.sss) }}</td></tr>
                  <tr><td class="pl-3">Cash Advance</td><td></td><td></td><td class="text-right">{{ num(d.payslip.cash_advance) }}</td></tr>
                  <tr>
                    <td class="pl-3">Other Deductions PNB/AXA</td><td></td>
                    <td class="text-right border-top">{{ num(d.payslip.other_deductions) }}</td>
                    <td class="text-right border-top">{{ num(d.payslip.deductions_total) }}</td>
                  </tr>
                  <tr class="net">
                    <td><strong>NET PAY:</strong></td><td></td><td></td>
                    <td class="text-right"><strong>{{ num(d.payslip.net) }}</strong></td>
                  </tr>
                </table>

                <div class="mt-3 small">Authorized by:</div>
                <div class="mt-3">
                  <div class="font-weight-bold text-uppercase">{{ payroll.authorized ? payroll.authorized.name : '' }}</div>
                  <div class="small">Accounting Officer</div>
                </div>
              </div>
            </div>

            <!-- Acknowledgement copy -->
            <div class="col-4">
              <div class="slip-box h-100">
                <div class="company small">{{ company.SHORT_NAME }}</div>
                <div class="text-center font-weight-bold small mb-2">ACKNOWLEDGEMENT COPY</div>
                <div class="small"><strong>EMPLOYEE:</strong> {{ d.manpower ? d.manpower.full_name : '' }}</div>
                <div class="small mb-3"><strong>For the period:</strong> {{ period }}</div>

                <table class="w-100 lines">
                  <tr><td>GROSS PAY:</td><td class="text-right">{{ num(d.payslip.gross) }}</td></tr>
                  <tr><td>DEDUCTIONS:</td><td class="text-right border-bottom">{{ num(d.payslip.deductions_total) }}</td></tr>
                  <tr><td><strong>NET PAY:</strong></td><td class="text-right"><strong>{{ num(d.payslip.net) }}</strong></td></tr>
                </table>

                <div class="mt-4 small">RECEIVED BY:</div>
                <div class="sign-line mt-5"></div>
                <div class="small text-center">SIGNATURE OVER PRINTED NAME</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { company_details } from '@/config'

  export default {
    name: 'OfficePayrollPayslip',
    data() {
      return {
        payroll: null,
        company: company_details,
        // Two identical copies per employee on one printed page.
        copies: ['EMPLOYEE COPY', 'OFFICE COPY'],
      }
    },
    computed: {
      period() {
        if (!this.payroll) return '';
        return this.payroll.date_from.other_formats.format_1 + ' - ' + this.payroll.date_to.other_formats.format_1;
      }
    },
    created() {
      this.get();
    },
    methods: {
      num(value, decimals = 2) {
        const n = Number(value);
        if (isNaN(n)) return value;
        return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      },
      position(d) {
        if (d.manpower && d.manpower.job_description && d.manpower.job_description.manpower_job_description) {
          return d.manpower.job_description.manpower_job_description.description;
        }
        return '';
      },
      printNow() {
        window.print();
      },
      get() {
        this.axios.get('office-payroll/payslips', { params: { id: this.$route.params.id } })
          .then(response => { this.payroll = response.data; })
          .catch(error => console.log(error));
      }
    }
  }
</script>

<style scoped>
  .payslip-page { background: #fff; }
  /* One sheet per employee: two copies stacked, cut line between them. */
  .payslip-sheet {
    margin-bottom: 32px;
    page-break-after: always;
    break-after: page;
  }
  .payslip-sheet:last-child {
    page-break-after: auto;
    break-after: auto;
    margin-bottom: 0;
  }
  .payslip {
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .payslip-second {
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px dashed #777;
  }
  .copy-label {
    text-align: right;
    font-size: 10px;
    font-weight: bold;
    letter-spacing: 0.5px;
    color: #555;
    margin-bottom: 2px;
  }
  .slip-box {
    border: 1px solid #000;
    padding: 8px 12px;
    font-size: 12px;
    color: #000;
  }
  .company {
    text-align: center;
    font-weight: bold;
    border-bottom: 1px solid #000;
    padding-bottom: 4px;
    margin-bottom: 6px;
  }
  .slip-box table td { padding: 1px 3px; vertical-align: top; }
  .lines .section td { padding-top: 4px; }
  .net td { border-top: 1px solid #000; padding-top: 4px; }
  .border-top { border-top: 1px solid #000; }
  .border-bottom { border-bottom: 1px solid #000; }
  .sign-line { border-top: 1px solid #000; }

  @media print {
    .no-print { display: none !important; }
    .payslip-page { padding: 0 !important; }
    .payslip-sheet { margin-bottom: 0; }
    /* Slightly tighter so both copies always fit one A4 page. */
    .slip-box { font-size: 11px; padding: 6px 10px; }
    .slip-box table td { padding: 0 3px; }
  }
</style>

<!-- Unscoped on purpose: scoped rules cannot reach html/body or @page. Bootstrap's
     print sheet forces body { min-width: 992px } which pushes the acknowledgement
     copy off an A4 sheet. -->
<style>
  @media print {
    @page { size: A4 portrait; margin: 10mm; }
    html, body { width: auto !important; min-width: 0 !important; background: #fff !important; }
  }
</style>
